import React, { useEffect, useState } from 'react'

import {
  Grid,
  Input,
  Row,
  Button,
  Form,
  Picker,
  Span,
  SelectForm
} from 'components'
import { useException, useLoading, useModal } from 'hooks'
import moment from 'moment'
import { useFormContext } from 'react-hook-form'
import { IResponseTypes } from 'services/core/genericEntityGST'
import {
  CentrosServices,
  IConsultaDocumentosGetByFilterParams,
  IDocumentosGetByFilterResponse,
  IDocumentosStatus,
  IEmpresas,
  IGenericGetAllCentro
} from 'services/GST/DocumentosItensSeguranca'
import { SPACING } from 'utils'

import { IForm } from '../../types'

interface ISearchFieldsProps {
  handleSubmitForm: (_data: IForm) => void
  refreshTable: (_data: IConsultaDocumentosGetByFilterParams) => void
  filterLists: IDocumentosGetByFilterResponse
  setDatesStatusOk: React.Dispatch<React.SetStateAction<boolean>>
  datesStatusOk: boolean
  isFirstGet: boolean
}

const SearchForm: React.FC<ISearchFieldsProps> = ({
  handleSubmitForm,
  refreshTable,
  filterLists,
  setDatesStatusOk,
  datesStatusOk,
  isFirstGet,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    reset,
    setError,
    clearErrors
  } = useFormContext<IForm>()

  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const { getException, createCustomException } = useException()

  const [centros, setCentros] = useState<IGenericGetAllCentro[]>([])

  const dataRetornoFinal = watch('dtRetornoFinal')
  const dataRetornoInicial = watch('dtRetornoInicial')
  const dataAnexadoFinal = watch('dtAnexadoFinal')
  const dataAnexadoInicial = watch('dtAnexadoInicial')

  const handleDatesVerification = () => {
    clearErrors()
    setDatesStatusOk(true)

    // Verificando se os campos das datas estão preenchidos corretamente
    if((dataRetornoInicial && !dataRetornoFinal) || (dataRetornoFinal && !dataRetornoInicial) ||
      (dataAnexadoInicial && !dataAnexadoFinal) || (dataAnexadoFinal && !dataAnexadoInicial)) {
      setDatesStatusOk(false)
    } else if (
      (dataRetornoFinal && !moment(dataRetornoFinal).isValid()) ||
      (dataRetornoInicial && !moment(dataRetornoInicial).isValid()) ||
      (dataAnexadoFinal && !moment(dataAnexadoFinal).isValid()) ||
      (dataAnexadoInicial && !moment(dataAnexadoInicial).isValid())) {
      setDatesStatusOk(false)
    } else {
      setDatesStatusOk(true)
    }

    // Datas Retorno Análise
    if ((dataRetornoInicial && !dataRetornoFinal)) {
      setError('dtRetornoFinal', { message: 'Adicione a data Final' })
    } else { clearErrors('dtRetornoFinal') }
    if ((!dataRetornoInicial && dataRetornoFinal)) {
      setError('dtRetornoInicial', { message: 'Adicione a data Inicial' })
    } else { clearErrors('dtRetornoInicial') }

    // Datas Anexo
    if ((dataAnexadoInicial && !dataAnexadoFinal)) {
      setError('dtAnexadoFinal', { message: 'Adicione a data Final' })
    } else { clearErrors('dtAnexadoFinal') }
    if ((!dataAnexadoInicial && dataAnexadoFinal)) {
      setError('dtAnexadoInicial', { message: 'Adicione a data Inicial' })
    } else { clearErrors('dtAnexadoInicial') }

    // Verificação de data inicial maior que final
    if ((dataRetornoInicial && dataRetornoFinal) &&
      (moment(new Date(dataRetornoInicial)).format('DD-MM-YYYY') > moment(new Date(dataRetornoFinal)).format('DD-MM-YYYY'))) 
    {
      setError('dtRetornoFinal', { message: 'Data final é menor que Data inicial'})
      setDatesStatusOk(false)
    } 

    if ((dataAnexadoInicial && dataAnexadoFinal) &&
      (moment(new Date(dataAnexadoInicial)).format('DD-MM-YYYY') > moment(new Date(dataAnexadoFinal)).format('DD-MM-YYYY'))) 
    {
      setError('dtAnexadoFinal', { message: 'Data final é menor que Data inicial'})
      setDatesStatusOk(false)
    }
  }

  const handleResetForm = () => {
    reset()
    refreshTable({})
  }

  const handleGetAllCentroPlantas = (): void => {
    setLoading('loading')

    CentrosServices()
      .getAllCentroPlantas()
      .then((response) => {

        if (response?.status !== 200 || response?.data?.tipo === IResponseTypes['alert']) {
          const { tipo, detalhe, mensagem, } = response?.data

          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao fazer a requisição',
            tipo: IResponseTypes[tipo]
          })
        }

        setCentros(response?.data?.entity?.semValidacao
          .sort((a: IGenericGetAllCentro, b: IGenericGetAllCentro) =>
            (a?.txDescricaoCentroPlanta > b?.txDescricaoCentroPlanta) ? 1 : -1)
        )
        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error',
        })
      })
  }

  useEffect(() => {
    handleDatesVerification()
  }, [
    dataRetornoInicial, dataRetornoFinal,
    dataAnexadoInicial, dataAnexadoFinal
  ])

  useEffect(() => {
    if (isFirstGet) {
      handleGetAllCentroPlantas()
    }
  }, [isFirstGet])

  return (
    <Form
      onSubmit={handleSubmit((data: IForm) => {
        handleSubmitForm({
          ...data,
          enviarListas: false,
          pageNumber: 1
        })
      })}
    >
      <Grid
        gridCols="1.5fr 1fr  2fr  2fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Empresa"
          placeholder="Selecione"
          clearSelect={true}
          formValue="empresaId"
          listOptions={
            filterLists?.entity?.empresas?.map((empresa: IEmpresas) => (
              { label: empresa.nmEmpresa, value: empresa.empresaId }
            )) || []
          }
        />
        <Span text="" marginTop={SPACING.spacing30} />
        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Centro"
          placeholder="Selecione"
          clearSelect={true}
          formValue="centroPlantaId"
          listOptions={
            centros?.map((centro: IGenericGetAllCentro) => (
              { label: centro?.txDescricaoCentroPlanta, value: centro?.idCentroPlanta }
            )) || []
          }
        />
        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Status"
          placeholder="Selecione"
          clearSelect={true}
          formValue="documentoStatus"
          listOptions={
            filterLists?.entity?.documentoStatus?.map((status: IDocumentosStatus) => {
              return { label: status.descricao, value: status.codigoDeStatus.toString() }
            }) || []
          }
        />
      </Grid>
      <Grid
        gridCols="1fr 1.5fr  2fr 2fr "
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <Input
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Contrato"
          placeholder="Nº"
          formValue="cdContrato"
        />
        <Input
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Fornecedor"
          placeholder="Fornecedor"
          formValue="nmFornecedor"
        />
        <SelectForm
          defaultValue={undefined}
          control={control}
          errors={errors}
          label="Item Segurança"
          placeholder="Selecione"
          clearSelect={true}
          formValue="itemSegurancaId"
          listOptions={
            filterLists?.entity?.itensSeguranca?.map((item) => (
              { label: item.nmItemSeguranca || item.dsItemSeguranca, value: item.idItemSeguranca }
            ))
          }
        />
      </Grid>
      <Grid
        gridCols="0.2fr 0fr 0.2fr 0fr 0.2fr 0fr 0.2fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
      >
        <Picker
          defaultValue={null}
          label="Retorno Análise"
          formValue='dtRetornoInicial'
          maxDate={watch('dtRetornoFinal') ? moment(watch('dtRetornoFinal')) : null}
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />
        <Span text="à" marginTop={SPACING.spacing30} />
        <Picker
          defaultValue={null}
          minDate={watch('dtRetornoInicial') ? moment(watch('dtRetornoInicial')) : null}
          label="."
          formValue='dtRetornoFinal'
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />
        <Span text="" marginTop={SPACING.spacing30} />
        <Picker
          defaultValue={null}
          maxDate={watch('dtAnexadoFinal') ? moment(watch('dtAnexadoFinal')) : null}
          label="Data Anexo"
          formValue='dtAnexadoInicial'
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />
        <Span text="à" marginTop={SPACING.spacing30} />
        <Picker
          defaultValue={null}
          minDate={watch('dtAnexadoInicial') ? moment(watch('dtAnexadoInicial')) : null}
          label="."
          formValue='dtAnexadoFinal'
          control={control}
          errors={errors}
          type={'dayMonthYear'}
        />
      </Grid>
      <Row justifyContent="flex-end">
        <Button
          variantButton="text"
          text="LIMPAR"
          width={'max-content'}
          onClick={handleResetForm}
          marginRight={SPACING.spacing20}
        />
        <Button
          variantButton="default"
          text="PESQUISAR"
          type="submit"
          width={'max-content'}
          disabled={!datesStatusOk}
        />
      </Row>
    </Form>
  )
}

export default SearchForm
